import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { backendClient } from "@/lib/backendClient";
import { cmsClient } from "@/lib/cmsClient";
import { useSessionStore } from "@/store/session";
import { useSelectionStore } from "@/store/selection";
import type { DealProductWithLocation } from "../deal.types";
import {
    parseDealProductWithLocation,
    parseDealResponse,
    parseLocationMap,
} from "../mappers/deal-product.parse";
import GET_DEAL_QUERY from "../graphql/GetDealProducts.graphql";
import GET_INSTALL_LOCATIONS_QUERY from "../graphql/GetInstallLocations.graphql";

export function useDealProducts() {
    const { t } = useTranslation();
    const dealId = useSessionStore((s) => s.dealId);
    const setBackendDealId = useSessionStore((s) => s.setBackendDealId);
    const setDealProducts = useSelectionStore((s) => s.setDealProducts);

    const {
        data: dealData,
        loading: dealLoading,
        error: dealError,
    } = useQuery<unknown>(GET_DEAL_QUERY, {
        client: backendClient,
        variables: { hubspotId: Number(dealId) },
        skip: !dealId,
    });

    const { data: locationsData, loading: locationsLoading } =
        useQuery<unknown>(GET_INSTALL_LOCATIONS_QUERY, { client: cmsClient });

    const locationMap = useMemo(
        () => parseLocationMap(locationsData),
        [locationsData],
    );

    const deal = useMemo(() => parseDealResponse(dealData), [dealData]);

    const products: DealProductWithLocation[] = useMemo(
        () =>
            deal.products.map((p) =>
                parseDealProductWithLocation(
                    p,
                    locationMap,
                    t("windowsStep.defaultLocation"),
                ),
            ),
        [deal, locationMap, t],
    );

    useEffect(() => {
        setDealProducts(products);
    }, [products]);

    useEffect(() => {
        if (deal.id !== null) setBackendDealId(deal.id);
    }, [deal.id]);

    return {
        products,
        loading: dealLoading || locationsLoading,
        error: dealError,
    };
}
